import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { disconnectGoogle, enableGmail, getGmailStatus, getGoogleStatus, pauseGmail, startGoogleConnect } from '@/data/client';
import { relativeTime } from '@/lib/format';
import { FormError } from './Modal';

export const useGoogleStatus = () => useQuery({ queryKey: ['google'], queryFn: getGoogleStatus, staleTime: 5 * 60_000 });

/** "Connect Google" — returns to the current page when Google is done. */
export function ConnectGoogleButton({ label = 'Connect Google Calendar' }: { label?: string }) {
  const { pathname, search } = useLocation();
  const go = useMutation({ mutationFn: () => startGoogleConnect(pathname + search) });
  return (
    <>
      <Button onClick={() => go.mutate()} disabled={go.isPending} iconLeft={<Icon name="calendar" size={16} />}>{go.isPending ? 'Opening Google…' : label}</Button>
      <FormError error={go.error} />
    </>
  );
}

export function GoogleConnectionCard() {
  const qc = useQueryClient();
  const { data } = useGoogleStatus();
  const off = useMutation({ mutationFn: disconnectGoogle, onSuccess: () => { for (const k of ['google', 'me']) void qc.invalidateQueries({ queryKey: [k] }); } });
  if (!data) return null;
  if (!data.configured) return <p className="crm-cell-muted">Google Calendar is not set up on this server yet.</p>;
  if (!data.connected) {
    return (
      <div className="crm-form">
        <p className="crm-cell-muted">Connect your Triton Google account to see colleagues' availability and send meeting invites from clientIQ.</p>
        <div><ConnectGoogleButton /></div>
      </div>
    );
  }
  return (
    <>
      <ul className="crm-people">
        <li>
          <span><Icon name="check-circle" size={14} /> Connected as <strong>{data.email}</strong></span>
          <Button variant="ghost" size="sm" onClick={() => off.mutate()} disabled={off.isPending}>Disconnect</Button>
        </li>
      </ul>
      <GmailCapture gmailGranted={data.gmailGranted} />
      {!data.draftsGranted && (
        <div className="gmail-capture">
          <h4><Icon name="send" size={14} /> Email drafts</h4>
          <p className="crm-cell-muted">Let the email assistant save drafts into your Gmail for you to review and send. clientIQ never sends email itself.</p>
          <ConnectGoogleButton label="Allow Gmail drafts" />
        </div>
      )}
    </>
  );
}

/* Client email capture for the signed-in member: only mail involving a known
   client is kept; everyone on the team can read it; any thread can be marked
   private. */
function GmailCapture({ gmailGranted }: { gmailGranted: boolean }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['gmail'], queryFn: getGmailStatus,
    refetchInterval: (q) => (q.state.data?.enabled && q.state.data.backfillState !== 'done' ? 5000 : false),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['gmail'] });
  const on = useMutation({ mutationFn: enableGmail, onSuccess: refresh });
  const off = useMutation({ mutationFn: pauseGmail, onSuccess: refresh });
  if (!data) return null;
  return (
    <div className="gmail-capture">
      <h4><Icon name="mail" size={14} /> Client email capture</h4>
      {!gmailGranted ? (
        <>
          <p className="crm-cell-muted">Put your client email on each account's timeline and in its Client Vault. Only mail with a known client is kept; personal and internal mail is ignored. Reconnect Google and allow Gmail.</p>
          <ConnectGoogleButton label="Allow Gmail access" />
        </>
      ) : !data.enabled ? (
        <>
          <p className="crm-cell-muted">Imports the last {data.backfillDays} days of client email, then checks every 10 minutes. The whole team can read captured client email; you can mark any thread private to remove it.</p>
          <Button size="sm" onClick={() => on.mutate()} disabled={on.isPending}>{data.captured ? 'Resume capture' : 'Start capturing client email'}</Button>
        </>
      ) : (
        <ul className="crm-people">
          <li>
            <span>
              {data.backfillState === 'done'
                ? <>On · {data.captured.toLocaleString()} client emails{data.lastSyncedAt ? ` · checked ${relativeTime(data.lastSyncedAt)}` : ''}</>
                : <>Importing the last {data.backfillDays} days… {data.captured.toLocaleString()} client emails so far</>}
            </span>
            <Button variant="ghost" size="sm" onClick={() => off.mutate()} disabled={off.isPending}>Pause</Button>
          </li>
        </ul>
      )}
      {data.error && <p className="crm-error">{data.error}</p>}
      <FormError error={on.error ?? off.error} />
    </div>
  );
}

const NOTICES: Record<string, { tone: 'ok' | 'warn'; text: (p: URLSearchParams) => string }> = {
  connected: { tone: 'ok', text: () => 'Google Calendar connected.' },
  cancelled: { tone: 'warn', text: () => 'Google connection cancelled.' },
  wrong_account: { tone: 'warn', text: (p) => `That Google account isn't ${p.get('expected') ?? 'your work email'}. Connect with your Triton Google account.` },
  missing_scopes: { tone: 'warn', text: () => 'Google didn’t grant calendar access. Connect again and leave the calendar boxes ticked.' },
  error: { tone: 'warn', text: () => 'Google connection failed. Try again.' },
};

/* Google's OAuth round-trip lands back on the page that started it with
   ?google=<result>. Show the result once, then drop the parameters. */
export function GoogleReturnNotice() {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [notice, setNotice] = useState<{ tone: string; text: string } | null>(null);
  useEffect(() => {
    const p = new URLSearchParams(search);
    const result = p.get('google');
    if (!result) return;
    const n = NOTICES[result] ?? NOTICES.error;
    setNotice({ tone: n.tone, text: n.text(p) });
    for (const k of ['google', 'me', 'team']) void qc.invalidateQueries({ queryKey: [k] });
    p.delete('google'); p.delete('expected');
    navigate({ pathname, search: p.toString() }, { replace: true });
  }, [search, pathname, navigate, qc]);
  if (!notice) return null;
  return (
    <div className={`crm-notice crm-notice--${notice.tone}`} role="status">
      <span>{notice.text}</span>
      <button type="button" className="lead-modal__x" aria-label="Dismiss" onClick={() => setNotice(null)}><Icon name="x" size={14} /></button>
    </div>
  );
}
